import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Package, Plus, Search, MoreHorizontal, Pencil, Trash2, Gauge, Clock, AlertTriangle, Shield, Calendar, DollarSign, Wrench, History, ExternalLink, User } from 'lucide-react';
import { toast } from 'sonner';

const EQ_TYPES = ['drill_rig', 'truck', 'pump', 'compressor', 'generator', 'trailer', 'other'];
const STATUSES = ['all', 'active', 'maintenance', 'out_of_service', 'retired'];
const MAINT_TYPES = ['oil_change', 'tire_rotation', 'filter', 'inspection', 'brake', 'transmission', 'alternator', 'windshield', 'battery', 'coolant', 'belt', 'hose', 'electrical', 'welding', 'paint', 'other'];

export default function EquipmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [maintDialogOpen, setMaintDialogOpen] = useState(false);
  const [maintHistoryOpen, setMaintHistoryOpen] = useState(false);
  const [jobsDialogOpen, setJobsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedEquip, setSelectedEquip] = useState(null);
  const [equipJobs, setEquipJobs] = useState([]);
  const [form, setForm] = useState({ name: '', type: 'truck', make: '', model: '', year: 2024, serial_number: '', license_plate: '', current_hours: 0, current_miles: 0, purchase_date: '', purchase_price: 0, warranty_expiry: '', warranty_provider: '', warranty_notes: '', next_maintenance_date: '', maintenance_interval_hours: 0, maintenance_interval_miles: 0, notes: '', assigned_to: '' });
  const [maintForm, setMaintForm] = useState({ type: 'oil_change', date: '', cost: 0, vendor: '', notes: '', hours_at_service: 0, miles_at_service: 0 });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/equipment', { params });
      setEquipment(res.data);
    } catch { toast.error(t('errors.loadingError')); }
    finally { setLoading(false); }
  }, [statusFilter, search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { api.get('/auth/users').then(res => setUsers(res.data)).catch(() => setUsers([])); }, []);

  const getResponsibleName = (eq) => users.find(u => u.id === eq.assigned_to)?.name || '';

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', type: 'truck', make: '', model: '', year: 2024, serial_number: '', license_plate: '', current_hours: 0, current_miles: 0, purchase_date: '', purchase_price: 0, warranty_expiry: '', warranty_provider: '', warranty_notes: '', next_maintenance_date: '', maintenance_interval_hours: 0, maintenance_interval_miles: 0, notes: '', assigned_to: '' });
    setDialogOpen(true);
  };

  const openEdit = (eq) => {
    setEditing(eq);
    setForm({ name: eq.name, type: eq.type, make: eq.make || '', model: eq.model || '', year: eq.year || 2024, serial_number: eq.serial_number || '', license_plate: eq.license_plate || '', current_hours: eq.current_hours || 0, current_miles: eq.current_miles || 0, purchase_date: eq.purchase_date || '', purchase_price: eq.purchase_price || 0, warranty_expiry: eq.warranty_expiry || '', warranty_provider: eq.warranty_provider || '', warranty_notes: eq.warranty_notes || '', next_maintenance_date: eq.next_maintenance_date || '', maintenance_interval_hours: eq.maintenance_interval_hours || 0, maintenance_interval_miles: eq.maintenance_interval_miles || 0, notes: eq.notes || '', assigned_to: eq.assigned_to || '' });
    setDialogOpen(true);
  };

  const openMaint = (eq) => {
    setSelectedEquip(eq);
    setMaintForm({ type: 'oil_change', date: new Date().toISOString().split('T')[0], cost: 0, vendor: '', notes: '', hours_at_service: eq.current_hours || 0, miles_at_service: eq.current_miles || 0 });
    setMaintDialogOpen(true);
  };

  const openMaintHistory = (eq) => {
    setSelectedEquip(eq);
    setMaintHistoryOpen(true);
  };

  const openJobs = async (eq) => {
    setSelectedEquip(eq);
    try {
      const res = await api.get(`/jobs/by-equipment/${eq.id}`);
      setEquipJobs(res.data);
    } catch {
      setEquipJobs([]);
    }
    setJobsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      if (editing) { await api.put(`/equipment/${editing.id}`, form); toast.success(t('equipment.updated')); }
      else { await api.post('/equipment', form); toast.success(t('equipment.created')); }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleMaintSave = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/equipment/${selectedEquip.id}/maintenance`, maintForm);
      toast.success(t('equipment.maintenanceCreated'));
      setMaintDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const handleStatusChange = async (id, status) => {
    try { await api.put(`/equipment/${id}`, { status }); toast.success(t('equipment.statusUpdated')); fetchData(); }
    catch { toast.error(t('errors.generic')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/equipment/${id}`); toast.success(t('equipment.deleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const statusLabelKey = (s) => {
    const map = { all: 'common.all', active: 'equipment.statuses.active', maintenance: 'equipment.statuses.maintenance', out_of_service: 'equipment.statuses.out_of_service', retired: 'equipment.statuses.retired' };
    return map[s] || s;
  };

  const getWarrantyStatus = (eq) => {
    if (!eq.warranty_expiry) return null;
    const expiry = new Date(eq.warranty_expiry);
    const now = new Date();
    return expiry >= now ? 'active' : 'expired';
  };

  const isMaintenanceDue = (eq) => {
    if (eq.next_maintenance_date) {
      const next = new Date(eq.next_maintenance_date);
      const now = new Date();
      const diff = (next - now) / (1000 * 60 * 60 * 24);
      if (diff <= 7) return true;
    }
    if (eq.maintenance_interval_hours && eq.current_hours >= eq.maintenance_interval_hours) return true;
    if (eq.maintenance_interval_miles && eq.current_miles >= eq.maintenance_interval_miles) return true;
    return false;
  };

  return (
    <div data-testid="equipment-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('equipment.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('equipment.registeredCount', { count: equipment.length })}</p>
        </div>
        <Button data-testid="new-equipment-btn" onClick={openCreate} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm self-start sm:self-auto">
          <Plus className="w-4 h-4 mr-2" /> {t('equipment.newEquipment')}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="search-equipment" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('equipment.searchPlaceholder')} className="pl-10 h-9 bg-white" />
        </div>
        <div className="tabs-scroll">
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-auto">
            <TabsList className="bg-white border border-gray-200 h-9">
              {STATUSES.map(s => (
                <TabsTrigger key={s} value={s} className="text-xs px-2 sm:px-2.5 data-[state=active]:bg-[#003366] data-[state=active]:text-white whitespace-nowrap">{t(statusLabelKey(s))}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : equipment.length === 0 ? (
            <div className="py-16 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('equipment.noEquipment')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('equipment.createFirstEquipment')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('equipment.title')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('equipment.equipmentType')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('equipment.make')}/{t('equipment.model')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('equipment.serialNumber')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden lg:table-cell">{t('equipment.hours')}/{t('equipment.miles')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden xl:table-cell">{t('equipment.sectionWarranty')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.map((eq) => (
                    <tr key={eq.id} data-testid={`equipment-row-${eq.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{eq.name}</p>
                            {eq.license_plate && <p className="text-xs text-gray-400">{t('equipment.licensePlate')}: {eq.license_plate}</p>}
                            {getResponsibleName(eq) ? (
                              <p className="text-xs text-[#003366] flex items-center gap-1 mt-0.5"><User className="w-3 h-3" />{getResponsibleName(eq)}</p>
                            ) : (
                              <p className="text-xs text-gray-300 italic mt-0.5">{t('equipment.noResponsible')}</p>
                            )}
                          </div>
                          {isMaintenanceDue(eq) && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium" title={t('equipment.maintenanceDue')}>
                              <Wrench className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">{t(`equipment.types.${eq.type}`, { defaultValue: eq.type })}</span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">{eq.make} {eq.model} {eq.year ? `(${eq.year})` : ''}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden lg:table-cell font-mono">{eq.serial_number || <span className="text-gray-300">-</span>}</td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{eq.current_hours || 0}h</span>
                          <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{(eq.current_miles || 0).toLocaleString()}mi</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden xl:table-cell">
                        {(() => {
                          const ws = getWarrantyStatus(eq);
                          if (!ws) return <span className="text-xs text-gray-300">-</span>;
                          return ws === 'active' 
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"><Shield className="w-3 h-3" />{t('equipment.warrantyActive')}</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-600 border border-red-200"><Shield className="w-3 h-3" />{t('equipment.warrantyExpired')}</span>;
                        })()}
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={eq.status} /></td>
                      <td className="px-5 py-3.5 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`eq-actions-${eq.id}`} className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openJobs(eq)} data-testid={`eq-view-jobs-${eq.id}`}><Wrench className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('equipment.assignedJobs')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openMaint(eq)} data-testid={`eq-register-maint-${eq.id}`}><AlertTriangle className="w-3.5 h-3.5 mr-2 text-amber-500" />{t('equipment.registerMaintenance')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openMaintHistory(eq)} data-testid={`eq-maint-history-${eq.id}`}><History className="w-3.5 h-3.5 mr-2 text-emerald-500" />Maintenance History</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(eq)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                            {eq.status === 'active' && <DropdownMenuItem onClick={() => handleStatusChange(eq.id, 'maintenance')}>{t('equipment.setMaintenance')}</DropdownMenuItem>}
                            {eq.status === 'maintenance' && <DropdownMenuItem onClick={() => handleStatusChange(eq.id, 'active')}>{t('equipment.activate')}</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => handleDelete(eq.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : equipment.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('equipment.noEquipment')}</p>
          </CardContent></Card>
        ) : (
          equipment.map((eq, idx) => (
            <Card key={eq.id} data-testid={`equipment-mobile-${eq.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{eq.name}</p>
                      {isMaintenanceDue(eq) && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium">
                          <Wrench className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[10px] font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t(`equipment.types.${eq.type}`, { defaultValue: eq.type })}</span>
                      {eq.license_plate && <span className="text-xs text-gray-400">{eq.license_plate}</span>}
                      {eq.serial_number && <span className="text-xs text-gray-400 font-mono">S/N: {eq.serial_number}</span>}
                    </div>
                    {eq.make && <p className="text-xs text-gray-500 mt-0.5">{eq.make} {eq.model} {eq.year ? `(${eq.year})` : ''}</p>}
                    {getResponsibleName(eq) ? (
                      <p className="text-xs text-[#003366] flex items-center gap-1 mt-0.5"><User className="w-3 h-3" />{getResponsibleName(eq)}</p>
                    ) : (
                      <p className="text-xs text-gray-300 italic mt-0.5">{t('equipment.noResponsible')}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openJobs(eq)}><Wrench className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('equipment.assignedJobs')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openMaint(eq)}><AlertTriangle className="w-3.5 h-3.5 mr-2 text-amber-500" />{t('equipment.registerMaintenance')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openMaintHistory(eq)}><History className="w-3.5 h-3.5 mr-2 text-emerald-500" />Maintenance History</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(eq)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(eq.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={eq.status} />
                    {(() => {
                      const ws = getWarrantyStatus(eq);
                      if (!ws) return null;
                      return ws === 'active' 
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700"><Shield className="w-2.5 h-2.5" />{t('equipment.warrantyActive')}</span>
                        : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600"><Shield className="w-2.5 h-2.5" />{t('equipment.warrantyExpired')}</span>;
                    })()}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{eq.current_hours || 0}h</span>
                    <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{(eq.current_miles || 0).toLocaleString()}mi</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow'] text-xl">{editing ? t('equipment.editEquipment') : t('equipment.newEquipment')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            {/* General Information */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-1">{t('equipment.sectionGeneral')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label>{t('equipment.equipmentName')} *</Label>
                  <Input data-testid="equipment-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.equipmentType')}</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{EQ_TYPES.map(tp => <SelectItem key={tp} value={tp}>{t(`equipment.types.${tp}`, { defaultValue: tp })}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.year')}</Label>
                  <Input type="number" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value) || 2024})} />
                </div>
                <div className="space-y-2"><Label>{t('equipment.make')}</Label><Input value={form.make} onChange={e => setForm({...form, make: e.target.value})} /></div>
                <div className="space-y-2"><Label>{t('equipment.model')}</Label><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} /></div>
                <div className="space-y-2"><Label>{t('equipment.serialNumber')}</Label><Input data-testid="equipment-serial" value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} /></div>
                <div className="space-y-2"><Label>{t('equipment.licensePlate')}</Label><Input value={form.license_plate} onChange={e => setForm({...form, license_plate: e.target.value})} /></div>
                <div className="col-span-2 space-y-2">
                  <Label>{t('equipment.responsibleUser')}</Label>
                  <Select value={form.assigned_to || 'none'} onValueChange={v => setForm({...form, assigned_to: v === 'none' ? '' : v})}>
                    <SelectTrigger data-testid="equipment-responsible-select"><SelectValue placeholder={t('equipment.selectResponsible')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('equipment.noResponsible')}</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Purchase Information */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-1 flex items-center gap-2"><DollarSign className="w-4 h-4 text-gray-400" />{t('equipment.sectionPurchase')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('equipment.purchaseDate')}</Label>
                  <Input data-testid="equipment-purchase-date" type="date" value={form.purchase_date} onChange={e => setForm({...form, purchase_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.purchasePrice')} ($)</Label>
                  <Input data-testid="equipment-purchase-price" type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => setForm({...form, purchase_price: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            </div>

            {/* Warranty */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-1 flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" />{t('equipment.sectionWarranty')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('equipment.warrantyExpiry')}</Label>
                  <Input data-testid="equipment-warranty-expiry" type="date" value={form.warranty_expiry} onChange={e => setForm({...form, warranty_expiry: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.warrantyProvider')}</Label>
                  <Input data-testid="equipment-warranty-provider" value={form.warranty_provider} onChange={e => setForm({...form, warranty_provider: e.target.value})} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>{t('equipment.warrantyNotes')}</Label>
                  <Textarea value={form.warranty_notes} onChange={e => setForm({...form, warranty_notes: e.target.value})} rows={2} placeholder={t('equipment.warrantyNotes') + '...'} />
                </div>
              </div>
            </div>

            {/* Usage & Odometer */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-1 flex items-center gap-2"><Gauge className="w-4 h-4 text-gray-400" />{t('equipment.sectionUsage')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>{t('equipment.currentHours')}</Label><Input type="number" value={form.current_hours} onChange={e => setForm({...form, current_hours: parseFloat(e.target.value) || 0})} /></div>
                <div className="space-y-2"><Label>{t('equipment.currentMiles')}</Label><Input type="number" value={form.current_miles} onChange={e => setForm({...form, current_miles: parseFloat(e.target.value) || 0})} /></div>
              </div>
            </div>

            {/* Maintenance Schedule */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 border-b border-gray-100 pb-1 flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" />{t('equipment.sectionMaintSchedule')}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>{t('equipment.nextMaintenanceDate')}</Label>
                  <Input data-testid="equipment-next-maint" type="date" value={form.next_maintenance_date} onChange={e => setForm({...form, next_maintenance_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.maintenanceIntervalHours')}</Label>
                  <Input type="number" min="0" value={form.maintenance_interval_hours} onChange={e => setForm({...form, maintenance_interval_hours: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="space-y-2">
                  <Label>{t('equipment.maintenanceIntervalMiles')}</Label>
                  <Input type="number" min="0" value={form.maintenance_interval_miles} onChange={e => setForm({...form, maintenance_interval_miles: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
            </div>

            <div className="space-y-2"><Label>{t('timecards.notes')}</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-equipment-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{editing ? t('common.save') : t('equipment.createEquipment')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={maintDialogOpen} onOpenChange={setMaintDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow'] text-xl">{t('equipment.registerMaintenance')}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500">{selectedEquip?.name}</p>
          <form onSubmit={handleMaintSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('equipment.maintenanceType')}</Label>
                <Select value={maintForm.type} onValueChange={v => setMaintForm({...maintForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MAINT_TYPES.map(tp => <SelectItem key={tp} value={tp}>{t(`equipment.maintTypes.${tp}`, { defaultValue: tp })}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t('timecards.date')}</Label><Input type="date" value={maintForm.date} onChange={e => setMaintForm({...maintForm, date: e.target.value})} /></div>
              <div className="space-y-2"><Label>{t('equipment.maintenanceCost')} ($)</Label><Input type="number" value={maintForm.cost} onChange={e => setMaintForm({...maintForm, cost: parseFloat(e.target.value) || 0})} min="0" step="0.01" /></div>
              <div className="space-y-2"><Label>{t('equipment.maintenanceVendor')}</Label><Input value={maintForm.vendor} onChange={e => setMaintForm({...maintForm, vendor: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>{t('timecards.notes')}</Label><Textarea value={maintForm.notes} onChange={e => setMaintForm({...maintForm, notes: e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMaintDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-maint-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{t('common.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assigned Jobs Dialog */}
      <Dialog open={jobsDialogOpen} onOpenChange={setJobsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Wrench className="w-5 h-5 text-[#3399FF]" />
              {t('equipment.assignedJobs')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">{selectedEquip?.name} {selectedEquip?.serial_number ? `(${selectedEquip.serial_number})` : ''}</p>
          {equipJobs.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">{t('equipment.noAssignedJobs')}</p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {equipJobs.map(j => (
                <div key={j.id} data-testid={`equip-job-${j.id}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => { setJobsDialogOpen(false); navigate(`/jobs/${j.id}`); }}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{j.title}</p>
                    <p className="text-xs text-gray-400 font-mono">{j.job_number}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={j.status} />
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setJobsDialogOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maintenance History Dialog */}
      <Dialog open={maintHistoryOpen} onOpenChange={setMaintHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-500" />
              Maintenance History
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{selectedEquip?.name} {selectedEquip?.serial_number ? `(S/N: ${selectedEquip.serial_number})` : ''}</p>
            <Button size="sm" variant="outline" onClick={() => { setMaintHistoryOpen(false); openMaint(selectedEquip); }} className="text-xs">
              <Plus className="w-3 h-3 mr-1" /> Register New
            </Button>
          </div>
          {(!selectedEquip?.maintenance_history || selectedEquip.maintenance_history.length === 0) ? (
            <div className="py-8 text-center">
              <Wrench className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-400">No maintenance records yet</p>
              <p className="text-xs text-gray-300 mt-1">Click "Register New" to add the first entry</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...(selectedEquip?.maintenance_history || [])].reverse().map((entry, idx) => (
                <div key={entry.id || idx} data-testid={`maint-entry-${idx}`} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                        <Wrench className="w-3 h-3" />
                        {(entry.type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      {entry.cost > 0 && (
                        <span className="text-xs font-semibold text-emerald-600">${entry.cost.toFixed(2)}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{entry.date || entry.created_at?.split('T')[0] || '-'}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    {entry.hours_at_service > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{entry.hours_at_service}h</span>}
                    {entry.miles_at_service > 0 && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{entry.miles_at_service.toLocaleString()} mi</span>}
                    {entry.vendor && <span>Vendor: {entry.vendor}</span>}
                  </div>
                  {entry.notes && <p className="text-xs text-gray-600 mt-1.5 bg-white p-2 rounded border border-gray-100">{entry.notes}</p>}
                </div>
              ))}
              <div className="pt-2 border-t border-gray-100 text-right">
                <p className="text-xs text-gray-500">
                  Total: {selectedEquip?.maintenance_history?.length || 0} records | 
                  Cost: ${(selectedEquip?.maintenance_history || []).reduce((s, e) => s + (e.cost || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintHistoryOpen(false)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
